/**
 * Shared file-type icon resolution and byte formatting utilities.
 * Used by both `asc-file-upload` (drag preview) and `asc-file-upload-item` (queue item).
 */

/**
 * Maps lowercased file extensions to a display category identifier.
 * The identifier is rendered as the coloured badge label on the file icon.
 */
export const FILE_ICON_MAP: Readonly<Record<string, string>> = {
  // Documents
  pdf: 'PDF',
  doc: 'DOC',
  docx: 'DOC',
  xls: 'XLS',
  xlsx: 'XLS',
  ppt: 'PPT',
  pptx: 'PPT',
  txt: 'TXT',
  csv: 'CSV',
  // Images
  jpg: 'JPG',
  jpeg: 'JPG',
  png: 'PNG',
  gif: 'GIF',
  svg: 'SVG',
  webp: 'IMG',
  avif: 'IMG',
  // Video
  mp4: 'MP4',
  mov: 'MOV',
  avi: 'AVI',
  webm: 'VID',
  // Audio
  mp3: 'MP3',
  wav: 'WAV',
  ogg: 'OGG',
  // Figma / design
  fig: 'FIG',
  sketch: 'SKT',
  // Archives
  zip: 'ZIP',
  rar: 'RAR',
  gz: 'GZ',
  tar: 'TAR',
  // Code
  js: 'JS',
  ts: 'TS',
  json: 'JSON',
  html: 'HTML',
  css: 'CSS',
};

/**
 * Hue backgrounds for each file type category badge.
 * Falls back to the brand primary colour for unknown types.
 */
export const FILE_ICON_COLORS: Readonly<Record<string, string>> = {
  PDF: 'var(--asc-bg-error-solid, #d2260f)',
  DOC: 'var(--asc-accent-blue-bg-strong, #153664)',
  XLS: 'var(--asc-accent-green-bg-strong, #064307)',
  PPT: 'var(--asc-accent-amber-bg-strong, #4F2E00)',
  TXT: 'var(--asc-fg-tertiary, #627278)',
  CSV: 'var(--asc-accent-green-bg-strong, #064307)',
  JPG: 'var(--asc-bg-brand-solid, #008577)',
  PNG: 'var(--asc-bg-brand-solid, #008577)',
  GIF: 'var(--asc-accent-iris-bg-strong, #2B2F68)',
  SVG: 'var(--asc-accent-amber-fg, #C27800)',
  IMG: 'var(--asc-bg-brand-solid, #008577)',
  MP4: 'var(--asc-accent-sky-fg, #05A2C6)',
  MOV: 'var(--asc-accent-sky-fg, #05A2C6)',
  AVI: 'var(--asc-accent-sky-fg, #05A2C6)',
  VID: 'var(--asc-accent-sky-fg, #05A2C6)',
  MP3: 'var(--asc-accent-plum-bg-strong, #362F5B)',
  WAV: 'var(--asc-accent-plum-bg-strong, #362F5B)',
  OGG: 'var(--asc-accent-plum-bg-strong, #362F5B)',
  FIG: 'var(--asc-bg-brand-solid, #008577)',
  SKT: 'var(--asc-accent-amber-fg, #C27800)',
  ZIP: 'var(--asc-fg-secondary, #49565A)',
  RAR: 'var(--asc-fg-secondary, #49565A)',
  GZ: 'var(--asc-fg-secondary, #49565A)',
  TAR: 'var(--asc-fg-secondary, #49565A)',
  JS: 'var(--asc-accent-amber-fg, #C27800)',
  TS: 'var(--asc-accent-blue-bg-strong, #153664)',
  JSON: 'var(--asc-fg-secondary, #49565A)',
  HTML: 'var(--asc-accent-red-fg, #F03D26)',
  CSS: 'var(--asc-accent-blue-fg, #5A8ACF)',
};

/**
 * Resolves the display badge label for a given filename.
 * Returns the extension label (e.g. `'PDF'`) or `'FILE'` for unknown extensions.
 */
export function resolveFileIcon(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  return FILE_ICON_MAP[ext] ?? 'FILE';
}

/**
 * Returns the badge background color for a resolved file icon type.
 */
export function resolveFileIconColor(iconType: string): string {
  return FILE_ICON_COLORS[iconType] ?? 'var(--asc-bg-brand-solid, #008577)';
}

/**
 * Formats a byte count to a human-readable string (e.g. `"6.4 MB"`).
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, i);
  return `${parseFloat(value.toFixed(1))} ${units[i]}`;
}
